import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Homepage from "./components/Homepage";
import ServicesPage from "./components/ServicesPage";
import ServiceLandingPage from "./components/ServiceLandingPage";
import Secure365Page from "./components/Secure365Page";
import AboutPage from "./components/AboutPage";
import ContactPage from "./components/ContactPage";
import BlogPage from "./components/BlogPage";
import IndustryPage from "./components/IndustryPage";
import IndustriesHubPage from "./components/IndustriesHubPage";
import NotFoundPage from "./components/NotFoundPage";
import ScrollToHash from "./components/ScrollToHash";
import InstagramSquare from "./components/posters/InstagramSquare";
import InstagramStory from "./components/posters/InstagramStory";
import FacebookPost from "./components/posters/FacebookPost";
import TicketAuth from "./components/TicketAuth";
import ResetPassword from "./components/ResetPassword";
import { AuthProvider, useAuth } from "./context/AuthContext";
import MobileCallCTA from "./components/MobileCallCTA";
import "./App.css";

// Lazy load ChatWidget - not needed immediately
const ChatWidget = lazy(() => import("./components/ChatWidget"));

// Lazy load pages not needed on initial homepage load
const GiveawayPage = lazy(() => import("./components/GiveawayPage"));
const CostCutterPage = lazy(() => import("./components/CostCutterPage"));
const AuthorPage = lazy(() => import("./components/AuthorPage"));

// Lazy load admin/dashboard components (only needed for authenticated users)
const TicketDashboard = lazy(() => import("./components/TicketDashboard"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const GMBPostsPage = lazy(() => import("./components/admin/GMBPostsPage"));
const LiveChatPage = lazy(() => import("./components/admin/LiveChatPage"));
const ChatAnalyticsPage = lazy(() => import("./components/admin/ChatAnalyticsPage"));
const AnalyticsPage = lazy(() => import("./components/admin/AnalyticsPage"));
const LinkedInManager = lazy(() => import("./components/admin/LinkedInManager"));
const LeadsDashboard = lazy(() => import("./components/admin/LeadsDashboard"));
const GBPManager = lazy(() => import("./components/admin/GBPManager"));
const CostCutterCRM = lazy(() => import("./components/CostCutterCRM"));
const SavingsPortal = lazy(() => import("./pages/SavingsPortal"));
const ProspectUpload = lazy(() => import("./pages/ProspectUpload"));

// Loading fallback for lazy components
const LazyLoadFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Wrapper to pass URL param to ServiceLandingPage
const ServicePageWrapper = () => {
  const { slug } = useParams();
  return <ServiceLandingPage serviceSlug={slug} />;
};

// Wrapper for /remote-office-support (no :slug param)
const RemoteOfficeWrapper = () => {
  return <ServiceLandingPage serviceSlug="remote-office-support" />;
};

// Wrapper for /law-firm-it-support-toronto
const LawFirmITWrapper = () => {
  return <ServiceLandingPage serviceSlug="law-firm-it-support-toronto" />;
};

// Wrapper for /medical-practice-it-support-toronto
const MedicalPracticeITWrapper = () => {
  return <ServiceLandingPage serviceSlug="medical-practice-it-support-toronto" />;
};

// Wrapper for /real-estate-accounting-it-support-toronto
const RealEstateAccountingITWrapper = () => {
  return <ServiceLandingPage serviceSlug="real-estate-accounting-it-support-toronto" />;
};

// Wrapper for /backup-disaster-recovery-toronto
const BackupDisasterRecoveryWrapper = () => {
  return <ServiceLandingPage serviceSlug="backup-disaster-recovery-toronto" />;
};

// Wrapper for /it-services-etobicoke
const EtobicokeITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-etobicoke" />;
};

// Wrapper for /computer-network-support-toronto
const NetworkSupportWrapper = () => {
  return <ServiceLandingPage serviceSlug="computer-network-support-toronto" />;
};

// Wrapper for /msp-toronto
const MSPTorontoWrapper = () => {
  return <ServiceLandingPage serviceSlug="msp-toronto" />;
};

// Wrapper for /it-services-vaughan
const VaughanITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-vaughan" />;
};

// Wrapper for /it-services-mississauga
const MississaugaITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-mississauga" />;
};

// Wrapper for /it-services-brampton
const BramptonITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-brampton" />;
};

// Wrapper for /it-services-richmond-hill
const RichmondHillITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-richmond-hill" />;
};

// Wrapper for /it-services-markham
const MarkhamITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-markham" />;
};

// Wrapper for /it-services-newmarket
const NewmarketITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-newmarket" />;
};

// Wrapper for /it-services-aurora
const AuroraITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-aurora" />;
};

// Wrapper for /it-services-oakville
const OakvilleITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-oakville" />;
};

// Wrapper for /it-services-burlington
const BurlingtonITWrapper = () => {
  return <ServiceLandingPage serviceSlug="it-services-burlington" />;
};

// Wrapper for /manufacturing-it-support
const ManufacturingITWrapper = () => {
  return <ServiceLandingPage serviceSlug="manufacturing-it-support" />;
};

// Wrapper for /saas-license-audit-toronto
const SaaSLicenseAuditWrapper = () => {
  return <ServiceLandingPage serviceSlug="saas-license-audit-toronto" />;
};

// Wrapper for /vendor-contract-renegotiation-toronto
const VendorContractWrapper = () => {
  return <ServiceLandingPage serviceSlug="vendor-contract-renegotiation-toronto" />;
};

// Wrapper for /telecom-internet-cost-reduction-toronto
const TelecomCostWrapper = () => {
  return <ServiceLandingPage serviceSlug="telecom-internet-cost-reduction-toronto" />;
};

// Lazy load Answer and Case Study pages
const AnswerPage = lazy(() => import("./components/AnswerPage"));
const AnswersHubPage = lazy(() => import("./components/AnswerPage").then(module => ({ default: module.AnswersHubPage })));
const CaseStudyPage = lazy(() => import("./components/CaseStudyPage"));
const CaseStudiesHubPage = lazy(() => import("./components/CaseStudyPage").then(module => ({ default: module.CaseStudiesHubPage })));

// Layout wrapper for pages with Navbar and Footer
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <ScrollToHash />
      <div className="flex-1">
        {children}
      </div>
      <Footer />
    </div>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/support" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Main Website Pages */}
      <Route path="/" element={<Layout><Homepage /></Layout>} />
      <Route path="/cost-cutter" element={<Layout><Suspense fallback={<LazyLoadFallback />}><CostCutterPage /></Suspense></Layout>} />
      <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
      <Route path="/services/:slug" element={<Layout><ServicePageWrapper /></Layout>} />
      <Route path="/remote-office-support" element={<Layout><RemoteOfficeWrapper /></Layout>} />
      <Route path="/law-firm-it-support-toronto" element={<Layout><LawFirmITWrapper /></Layout>} />
      <Route path="/medical-practice-it-support-toronto" element={<Layout><MedicalPracticeITWrapper /></Layout>} />
      <Route path="/real-estate-accounting-it-support-toronto" element={<Layout><RealEstateAccountingITWrapper /></Layout>} />
      <Route path="/backup-disaster-recovery-toronto" element={<Layout><BackupDisasterRecoveryWrapper /></Layout>} />
      <Route path="/it-services-etobicoke" element={<Layout><EtobicokeITWrapper /></Layout>} />
      <Route path="/computer-network-support-toronto" element={<Layout><NetworkSupportWrapper /></Layout>} />
      <Route path="/msp-toronto" element={<Layout><MSPTorontoWrapper /></Layout>} />
      <Route path="/it-services-vaughan" element={<Layout><VaughanITWrapper /></Layout>} />
      <Route path="/it-services-mississauga" element={<Layout><MississaugaITWrapper /></Layout>} />
      <Route path="/it-services-brampton" element={<Layout><BramptonITWrapper /></Layout>} />
      <Route path="/it-services-richmond-hill" element={<Layout><RichmondHillITWrapper /></Layout>} />
      <Route path="/it-services-markham" element={<Layout><MarkhamITWrapper /></Layout>} />
      <Route path="/it-services-newmarket" element={<Layout><NewmarketITWrapper /></Layout>} />
      <Route path="/it-services-aurora" element={<Layout><AuroraITWrapper /></Layout>} />
      <Route path="/it-services-oakville" element={<Layout><OakvilleITWrapper /></Layout>} />
      <Route path="/it-services-burlington" element={<Layout><BurlingtonITWrapper /></Layout>} />
      <Route path="/manufacturing-it-support" element={<Layout><ManufacturingITWrapper /></Layout>} />
      <Route path="/saas-license-audit-toronto" element={<Layout><SaaSLicenseAuditWrapper /></Layout>} />
      <Route path="/vendor-contract-renegotiation-toronto" element={<Layout><VendorContractWrapper /></Layout>} />
      <Route path="/telecom-internet-cost-reduction-toronto" element={<Layout><TelecomCostWrapper /></Layout>} />
      <Route path="/secure365" element={<Layout><Secure365Page /></Layout>} />
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
      <Route path="/blog/:slug" element={<Layout><BlogPage /></Layout>} />
      
      {/* Answer Pages - AI Optimized Knowledge Base */}
      <Route path="/answers" element={<Layout><Suspense fallback={<LazyLoadFallback />}><AnswersHubPage /></Suspense></Layout>} />
      <Route path="/answers/:slug" element={<Layout><Suspense fallback={<LazyLoadFallback />}><AnswerPage /></Suspense></Layout>} />
      
      {/* Case Studies */}
      <Route path="/case-studies" element={<Layout><Suspense fallback={<LazyLoadFallback />}><CaseStudiesHubPage /></Suspense></Layout>} />
      <Route path="/case-studies/:slug" element={<Layout><Suspense fallback={<LazyLoadFallback />}><CaseStudyPage /></Suspense></Layout>} />
      <Route path="/giveaway" element={<Layout><Suspense fallback={<LazyLoadFallback />}><GiveawayPage /></Suspense></Layout>} />
      
      {/* Author Pages */}
      <Route path="/author/:slug" element={<Layout><Suspense fallback={<LazyLoadFallback />}><AuthorPage /></Suspense></Layout>} />
      <Route path="/author" element={<Navigate to="/author/alex-dayan" replace />} />
      
      {/* Industry Pages */}
      <Route path="/industries" element={<Layout><IndustriesHubPage /></Layout>} />
      <Route path="/industries/:industry" element={<Layout><IndustryPage /></Layout>} />
      
      {/* Poster Pages (No Layout - for downloading) */}
      <Route path="/poster/instagram" element={<InstagramSquare />} />
      <Route path="/poster/story" element={<InstagramStory />} />
      <Route path="/poster/facebook" element={<FacebookPost />} />
      
      {/* Ticket Support System */}
      <Route path="/support" element={<TicketAuth />} />
      <Route path="/support/reset-password/:token" element={<ResetPassword />} />
      <Route path="/support/dashboard" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <TicketDashboard />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/support/admin" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <AdminDashboard />
          </Suspense>
        </ProtectedRoute>
      } />
      
      {/* Admin Tools */}
      <Route path="/admin/gmb-posts" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <GMBPostsPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/live-chat" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <LiveChatPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/chat-analytics" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <ChatAnalyticsPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <AnalyticsPage />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/linkedin" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <LinkedInManager />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <LeadsDashboard />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/gbp" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <GBPManager />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/upload/:token" element={<Suspense fallback={<LazyLoadFallback />}><ProspectUpload /></Suspense>} />
      <Route path="/savings-portal/:token" element={<Suspense fallback={<LazyLoadFallback />}><SavingsPortal /></Suspense>} />
      <Route path="/admin/cost-cutter" element={
        <ProtectedRoute>
          <Suspense fallback={<LazyLoadFallback />}>
            <CostCutterCRM />
          </Suspense>
        </ProtectedRoute>
      } />

      {/* Legacy URL Redirects - old pages that have moved */}
      <Route path="/admin" element={<Navigate to="/support/admin" replace />} />
      <Route path="/outsourced-it-service" element={<Navigate to="/services#outsourced" replace />} />
      <Route path="/new-and-refurbished-hardware" element={<Navigate to="/services#hardware" replace />} />
      <Route path="/managed-services" element={<Navigate to="/services/managed-it-toronto" replace />} />
      <Route path="/business-continuity-planning" element={<Navigate to="/backup-disaster-recovery-toronto" replace />} />
      <Route path="/business-continuity" element={<Navigate to="/backup-disaster-recovery-toronto" replace />} />
      <Route path="/it-consulting" element={<Navigate to="/services#consulting" replace />} />
      <Route path="/cloud-solutions" element={<Navigate to="/services/cloud-solutions-toronto" replace />} />
      <Route path="/cybersecurity" element={<Navigate to="/services/cybersecurity-toronto" replace />} />
      <Route path="/network-setup" element={<Navigate to="/services/network-setup-toronto" replace />} />
      <Route path="/computer-repair" element={<Navigate to="/services/it-support-toronto" replace />} />

      {/* 404 Catch-All - MUST be last */}
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
  );
}

function PublicChat() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/savings-portal') || pathname.startsWith('/upload/')) return null;
  return <Suspense fallback={null}><ChatWidget /></Suspense>;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <PublicChat />
            <MobileCallCTA />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
